import * as Yup from 'yup';
import { CountryCode, parsePhoneNumber, ParseError, validatePhoneNumberLength } from 'libphonenumber-js';
import { Agent, AgentStatus } from '../Agent.types';
import { Laender, COUNTRYCODE } from '../Country.types';
import AgentInput from '../initValues/AgentInput';

// 1. BIC-Validierungsfunktion
function validateBIC(bic: string | undefined, countryId: number): boolean {
  if (!bic) return false;

  // Allgemeine BIC-Struktur (8 oder 11 Zeichen)
  const bicRegex = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
  if (!bicRegex.test(bic)) return false;

  // Ländercode aus dem BIC extrahieren (Position 5-6)
  const bicCountryCode = bic.substring(4, 6);

  // Land des Agents aus der COUNTRYCODE-Mapping ermitteln
  const countryEntry = Object.entries(COUNTRYCODE).find(([, data]) => data.id === countryId);
  if (!countryEntry) return false;

  const [, countryData] = countryEntry;
  return bicCountryCode === countryData.code;
}

// 2. Existierende Hilfsfunktionen
function validateStartDate(userInputStartdate: string | undefined): boolean {
  if (userInputStartdate) {
    const inputIsADate = new Date(userInputStartdate);
    if (!inputIsADate) return false;

    const minYear = new Date('2000-12-31').getFullYear();
    const maxYear = new Date('2100-12-31').getFullYear();
    const inputDateYear = inputIsADate.getFullYear();
    if (inputDateYear < minYear || inputDateYear > maxYear) return false;

    return true;
  }
  if (userInputStartdate === AgentInput.start_date) {
    return true;
  }
  return false;
}

function validateIBAN(iban: string | undefined): boolean {
  if (iban) {
    const ibanRemoveSpaces = iban.replace(/\s/g, '');
    const countryCode = ibanRemoveSpaces.slice(0, 2);
    const countryIbanLength: any = {
      DE: 22,
      IT: 27,
      CH: 21,
      AT: 20,
      ES: 24,
      LT: 20,
    };

    if (!countryIbanLength[countryCode] || ibanRemoveSpaces.length !== countryIbanLength[countryCode]) {
      return false;
    }

    const formattedIban = ibanRemoveSpaces.slice(4) + ibanRemoveSpaces.slice(0, 4);
    let formattedIbanNumeric = '';

    for (let i = 0; i < formattedIban.length; i += 1) {
      const charCode = formattedIban.charCodeAt(i);
      if (charCode >= 65 && charCode <= 90) {
        formattedIbanNumeric += (charCode - 55).toString();
      } else {
        formattedIbanNumeric += formattedIban[i];
      }
    }

    return BigInt(formattedIbanNumeric) % BigInt(97) === BigInt(1);
  }
  return false;
}

// 3. Basis-Validierungsschemata
const validatePhoneFax = Yup.string()
  .min(2, 'min. 2 Zeichen')
  .test('uniq', 'zu Lang', (value, context) => {
    if (value) {
      let isValid = false;
      try {
        const isToLong = validatePhoneNumberLength(
          value,
          COUNTRYCODE[context.parent.country_id as Laender].code as CountryCode,
        );
        isValid = !(isToLong === 'TOO_LONG');
      } catch (parseError) {
        if (parseError instanceof ParseError) {
          console.error(`status`, parseError.message);
        }
      }
      return isValid;
    }
    return true;
  })
  .test('uniq', 'Nummer ungültig', (value, context) => {
    if (value) {
      let isValid = false;
      try {
        const formatetPhoneNumber = parsePhoneNumber(
          value,
          COUNTRYCODE[context.parent.country_id as Laender].code as CountryCode,
        );
        isValid = formatetPhoneNumber.isValid();
      } catch (parseError) {
        if (parseError instanceof ParseError) {
          console.error(`status`, parseError.message);
        }
      }
      return isValid;
    }
    return true;
  })
  .nullable();

const stringMax128required = Yup.string().max(128, 'max. 128 Zeichen').required('Pflichtfeld');
const stringMax128nullable = Yup.string().max(128, 'max. 128 Zeichen').nullable();
const stringNullable = Yup.string().nullable();

// 4. BIC-Validierungsschema
const bicValidation = Yup.string()
  .max(11, 'BIC darf maximal 11 Zeichen haben')
  .test('valid-bic-format', 'Ungültiges BIC-Format (Beispiel: DEUTDEFF oder UNCRITMMXXX)', (value, context) => {
    if (!value) return true;
    return validateBIC(value, context.parent.country_id);
  })
  .test('country-match', 'BIC-Ländercode stimmt nicht mit Agentenland überein', (value, context) => {
    if (!value) return true;
    return validateBIC(value, context.parent.country_id);
  })
  .test('required', 'Pflichtfeld', (value) => {
    if (value) {
      return true;
    }
    return false;
  })
  .nullable();

// 5. Status-abhängige Validierungen
const stringMax128when = Yup.string()
  .max(128, 'max 128 Zeichen')
  .when('status_id', {
    is: AgentStatus.active,
    then: Yup.string().max(128, 'max 128 Zeichen').required('Pflichtfeld').typeError('Pflichtfeld'),
  })
  .when('status_id', {
    is: AgentStatus.inProcess,
    then: Yup.string().nullable(),
  })
  .when('status_id', {
    is: AgentStatus.failed,
    then: Yup.string().nullable(),
  })
  .when('status_id', {
    is: AgentStatus.dismissed,
    then: Yup.string().nullable(),
  });

const stringWhen = Yup.string()
  .when('status_id', {
    is: AgentStatus.active,
    then: Yup.string()
      .test('uniq', 'Ungültiges Datum', (value) => validateStartDate(value))
      .required('Pflichtfeld')
      .typeError('Pflichtfeld'),
  })
  .when('status_id', {
    is: AgentStatus.inProcess,
    then: Yup.string()
      .test('uniq', 'Ungültiges Datum', (value) => validateStartDate(value))
      .nullable(),
  })
  .when('status_id', {
    is: AgentStatus.dismissed,
    then: Yup.string().nullable(),
  })
  .when('status_id', {
    is: AgentStatus.failed,
    then: Yup.string().nullable(),
  });

const numberMax10required = Yup.number()
  .integer('Keine Kommazahlen')
  .max(10)
  .required('Pflichtfeld')
  .typeError('Pflichtfeld');

// 6. Haupt-Validierungsschema
function ValidationSchema(agents: Agent[]) {
  const agentInProcessValidationSchema = Yup.object()
    .shape({
      name: stringMax128required,
      surname: stringMax128required,
      agent_id: Yup.number().integer('Keine Kommazahlen').max(9999, 'max 4 Zeichen').nullable(),
      birthday: Yup.string().required('Pflichtfeld'),
      status_id: Yup.number().min(1).max(20).required('Pflichtfeld'),
      is_male: Yup.number().min(0).max(1).required('Pflichtfeld'),
      notes: stringNullable,
      phone: validatePhoneFax,
      mobile: validatePhoneFax,
      fax: validatePhoneFax,
      email_corp: Yup.string()
        .max(128, 'max 128 Zeichen')
        .required('Pflichtfeld')
        .test('uniq', 'Email ist bereits vorhanden', (value, context) => {
          const corpEmailSearch = agents.filter((item) => item.email_corp === value);
          if (context.parent.agent_id === null && corpEmailSearch.length > 0) {
            return false;
          }
          return true;
        })
        .test('uniq', 'Validierung fehlgeschlagen siehe Info', (value) => {
          if (value) {
            return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(value);
          }
          return false;
        })
        .nullable(),
      email_private: Yup.string()
        .max(128, 'max 128 Zeichen')
        .required('Pflichtfeld')
        .test('uniq', 'Validierung fehlgeschlagen siehe Info', (value) => {
          if (value) {
            return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(value);
          }
          return false;
        }),
      myjack_login: Yup.string()
        .max(100, 'max 100 Zeichen')
        .when('status_id', {
          is: AgentStatus.active,
          then: Yup.string().max(100, 'max 100 Zeichen').required('Pflichtfeld').typeError('Pflichtfeld'),
        })
        .when('status_id', {
          is: AgentStatus.inProcess,
          then: Yup.string().nullable(),
        })
        .when('status_id', {
          is: AgentStatus.failed,
          then: Yup.string().nullable(),
        })
        .when('status_id', {
          is: AgentStatus.dismissed,
          then: Yup.string().nullable(),
        }),
      rank_id: numberMax10required,
      level_id: numberMax10required,
      branch_office_id: numberMax10required,
      jackplus_id: Yup.number()
        .integer('Keine Kommazahlen')
        .max(999999, 'max. 6 Stellen')
        .test('uniq', 'bereits vorhanden', (value, context) => {
          let porbeArr = agents;
          if (context.parent.agent_id) {
            porbeArr = agents.filter((agent) => agent.agent_id !== context.parent.agent_id);
          }
          const otherJackId = porbeArr.find((agent) => value && agent.jackplus_id === value);
          return !otherJackId;
        })
        .when('status_id', {
          is: AgentStatus.active,
          then: Yup.number()
            .test('required', 'Pflichtfeld', (value) => {
              if (value) {
                return true;
              }
              return false;
            })
            .nullable()
            .typeError('Falscher Datentyp'),
        })
        .when('status_id', {
          is: AgentStatus.inProcess,
          then: Yup.number().nullable(),
        }),
      agent_account_nr: Yup.number()
        .max(9999999, 'max. 7 Stellen')
        .integer('Keine Kommazahlen')
        .test('uniq', 'bereits vorhanden', (value, context) => {
          let probeArrAccountNr = agents;
          if (context.parent.agent_id) {
            probeArrAccountNr = agents.filter((agent) => agent.agent_id !== context.parent.agent_id);
          }
          const otherAgentAccountNr = probeArrAccountNr.find((agent) => value && agent.agent_account_nr === value);
          return !otherAgentAccountNr;
        })
        .when('status_id', {
          is: AgentStatus.active,
          then: Yup.number()
            .test('required', 'Pflichtfeld', (value) => {
              if (value) {
                return true;
              }
              return false;
            })
            .nullable()
            .typeError('Falscher Datentyp'),
        })
        .when('status_id', {
          is: AgentStatus.inProcess,
          then: Yup.number().nullable(),
        }),
      contract_type_id: Yup.number()
        .integer('Keine Kommazahlen')
        .max(10)
        .when('status_id', {
          is: AgentStatus.active,
          then: Yup.number().max(10).required('Pflichtfeld').typeError('Pflichtfeld'),
        })
        .when('status_id', {
          is: AgentStatus.inProcess,
          then: Yup.number().max(10).required('Pflichtfeld').typeError('Pflichtfeld'),
        })
        .required('Pflichtfeld')
        .typeError('Pflichtfeld'),
      address: stringMax128required,
      zip: Yup.string()
        .matches(/^[0-9]+$/, 'nur Zahlen')
        .min(1, 'min 1 Zeichen')
        .max(9, 'max 9 Zeichen')
        .required('Pflichtfeld'),
      city: stringMax128required,
      country_id: numberMax10required,
      address2: stringMax128nullable,
      zip2: Yup.string()
        .matches(/^[0-9]+$/, 'nur Zahlen')
        .min(1, 'max 1 Zeichen')
        .max(9, 'min 9 Zeichen')
        .nullable(),
      city2: stringMax128nullable,
      country2_id: Yup.number().integer('Keine Kommazahlen').max(10).nullable(),
      tax_nr: stringMax128when,
      ust_nr: Yup.string()
        .max(128, 'max 128 Zeichen')
        .when(['status_id', 'wirtschafts_id'], {
          is: (status_id: number, wirtschafts_id: string | null) => status_id === AgentStatus.active && !wirtschafts_id,
          then: Yup.string()
            .max(128, 'max 128 Zeichen')
            .required('Bei aktivem Status muss entweder USt-Nr oder Wirtschafts-ID angegeben werden')
            .typeError('Pflichtfeld'),
          otherwise: Yup.string().max(128, 'max 128 Zeichen').nullable(),
        })
        .when('status_id', {
          is: AgentStatus.inProcess,
          then: Yup.string().nullable(),
        })
        .when('status_id', {
          is: AgentStatus.failed,
          then: Yup.string().nullable(),
        })
        .when('status_id', {
          is: AgentStatus.dismissed,
          then: Yup.string().nullable(),
        }),
      wirtschafts_id: Yup.string()
        .max(20, 'max. 20 Zeichen')
        .test('format', 'Ungültiges Format (Beispiel: DE123456789-00001)', (value) => {
          if (!value) return true;
          // Entferne alle Leerzeichen
          const cleanValue = value.replace(/\s/g, '');
          return /^DE\d{9}-\d{5}$/.test(cleanValue);
        })
        .test('consistency', 'Basis-Nummer stimmt nicht mit USt-Nr überein', (value, context) => {
          if (!value) return true;

          // Nur für deutsche Agenten prüfen
          if (context.parent.country_id !== 1) return true;

          const ustNr = context.parent.ust_nr;
          if (!ustNr) return true;

          // Entferne Leerzeichen und extrahiere Basis-Nummern
          const cleanWirtschaftsId = value.replace(/\s/g, '');
          const cleanUstNr = ustNr.replace(/\s/g, '');

          // Extrahiere 9-stellige Basis-Nummern
          const wirtschaftsBase = cleanWirtschaftsId.substring(2, 11);
          const ustBase = cleanUstNr.replace(/^DE/, '').substring(0, 9);

          return wirtschaftsBase === ustBase;
        })
        .nullable(),
      iban: Yup.string()
        .max(128, 'max 128 Zeichen')
        .when('status_id', {
          is: AgentStatus.active,
          then: Yup.string()
            .max(128, 'max 128 Zeichen')
            .required('Pflichtfeld')
            .test('uniq', 'Validierung fehlgeschlagen siehe Info', (value) => validateIBAN(value))
            .typeError('Pflichtfeld'),
        })
        .when('status_id', {
          is: AgentStatus.inProcess,
          then: Yup.string().nullable(),
        })
        .when('status_id', {
          is: AgentStatus.dismissed,
          then: Yup.string().nullable(),
        })
        .when('status_id', {
          is: AgentStatus.failed,
          then: Yup.string().nullable(),
        }),
      bic: Yup.string().when('status_id', {
        is: AgentStatus.active,
        then: bicValidation,
        otherwise: Yup.string()
          .nullable()
          .max(11, 'BIC darf maximal 11 Zeichen haben')
          .test('valid-bic-format', 'Ungültiges BIC-Format (Beispiel: DEUTDEFF oder UNCRITMMXXX)', (value, context) => {
            if (!value) return true;
            return validateBIC(value, context.parent.country_id);
          })
          .test('country-match', 'BIC-Ländercode stimmt nicht mit Agentenland überein', (value, context) => {
            if (!value) return true;
            return validateBIC(value, context.parent.country_id);
          }),
      }),
      business_owner: stringMax128nullable,
      start_date: stringWhen,
      is_small_business: Yup.number()
        .min(0)
        .max(2)
        .required('Pflichtfeld')
        .when('status_id', {
          is: AgentStatus.active,
          then: Yup.number().test(
            'active-status-taxation',
            'Bei aktivem Status muss eine konkrete Besteuerungsart gewählt werden (Regel- oder Kleinunternehmerbesteuerung)',
            (value) => value === 0 || value === 1,
          ),
        }),
      bank_name: stringMax128nullable,
      roles: Yup.array()
        .of(Yup.string().required('Rolle ist erforderlich'))
        .max(5, 'Maximal 5 Rollen erlaubt')
        .nullable(),
    })
    .test(
      'ust-or-wirtschafts-id-required',
      'Bei aktivem Status muss entweder USt-Nr oder Wirtschafts-ID angegeben werden',
      function checkUstOrWirtschaftsId(values) {
        // Nur bei aktivem Status prüfen
        if (values.status_id !== AgentStatus.active) {
          return true;
        }

        // Mindestens eines der Felder muss ausgefüllt sein
        if (!values.ust_nr && !values.wirtschafts_id) {
          // Fehler für beide Felder setzen
          return this.createError({
            path: 'ust_nr',
            message: 'Bei aktivem Status muss entweder USt-Nr oder Wirtschafts-ID angegeben werden',
          });
        }

        return true;
      },
    );

  return agentInProcessValidationSchema;
}

export default ValidationSchema;
